import type {BookingProcess} from 'types/app'
import type {PackageItem} from 'types/entities/PackageItem'
import type {PackagesItemsResponse, SelectedPackageCreditItem} from 'types/entities/PackagesItems'
import {getCreditAndUpsellItems, isCreditPackageItem, isSingleUnitCreditPackage, matchesCreditCategory} from 'utils/offers/addItemToCalculationUtils'

export const creditQuantity = (value: number): number =>
    Number.isFinite(Number(value)) ? Math.max(0, Math.floor(Number(value))) : 0

export const getCreditQuantityLimits = (item: PackageItem, adults: number) => {
    const adultCount = creditQuantity(adults)
    const itemMaximum = creditQuantity(item.maximum_persons_count)
    const maximum = itemMaximum > 0 ? Math.min(adultCount, itemMaximum) : adultCount
    const minimum = maximum === 0 ? 0 : Math.min(maximum, Math.max(1, creditQuantity(item.minimum_persons_count)))
    return {minimum, maximum}
}

export const clampCreditQuantity = (item: PackageItem, quantity: number, adults: number): number => {
    const {minimum, maximum} = getCreditQuantityLimits(item, adults)
    const count = creditQuantity(quantity)
    return count === 0 ? 0 : Math.min(maximum, Math.max(minimum, count))
}

export const getDefaultCreditQuantity = (item: PackageItem, adults: number): number =>
    clampCreditQuantity(item, isSingleUnitCreditPackage(item) ? 1 : adults, adults)

export const getAvailableCreditItems = (catalog: PackagesItemsResponse | null | undefined, category?: string | null): PackageItem[] =>
    getCreditAndUpsellItems(catalog, category).filter((item) => isCreditPackageItem(item) && Number(item.is_active) === 1)

/** Read old saved drafts too; the independent selection wins over legacy upsells. */
export const getSelectedBookingCredits = (process: Pick<BookingProcess, 'upsell' | 'selectedPackageCreditItems'> & Partial<Pick<BookingProcess, 'filter'>>): SelectedPackageCreditItem[] => {
    const selections = new Map<number, SelectedPackageCreditItem>()
    for (const item of process.upsell) {
        if (isCreditPackageItem(item.packageItem)) {
            selections.set(item.packageItem.id, {packageItem: item.packageItem, quantity: item.count})
        }
    }
    for (const item of process.selectedPackageCreditItems ?? []) {
        selections.set(item.packageItem.id, item)
    }
    return [...selections.values()].filter((item) => matchesCreditCategory(item.packageItem, process.filter?.category))
}

/** Refresh metadata/prices, drop unavailable credits, and never silently add a purchase. */
export const reconcileCreditItems = (
    selected: SelectedPackageCreditItem[],
    catalog: PackagesItemsResponse | null,
    adults: number,
    category?: string | null
): SelectedPackageCreditItem[] => {
    const available = new Map(getAvailableCreditItems(catalog, category).map((item) => [item.id, item]))
    const result = new Map<number, SelectedPackageCreditItem>()
    for (const selection of selected) {
        const item = available.get(selection.packageItem.id)
        if (!item) continue
        const quantity = clampCreditQuantity(item, selection.quantity, adults)
        if (quantity > 0) result.set(item.id, {packageItem: item, quantity})
    }
    return [...result.values()]
}
